<template v-if="modelValue?.phases?.length">
  <section class="my-12">
    <div>
      <div class="mb-6 flex flex-wrap items-center justify-between border border-neutral-300 bg-neutral-35 p-5">
        <span>{{ wt('estimation', 'table', 'title') }}</span>
        <span class="text-lg -pr-5">{{ formatInitialHours(props.serviceCalculator.estimatedTotalHours) }} h</span>
      </div>

      <h4 class="py-4 text-base">{{ wt('estimation', 'table', 'subtitle') }}</h4>

      <table class="core-service-estimation-table">
        <tbody>
          <tr
            v-for="(item, index) in modelValue?.phases"
            :key="index"
            class="w-full sm:px-5"
            :class="{ active: item.active, 'border-t border-neutral-300': index !== 0 }"
            :id="`${props.role.toLowerCase()}_service-estimation-phase-${index}`">
            <td class="pb-0 core-form-checkbox">
              <input v-model="item.active" type="checkbox" class="relative inline my-2.5 h-6 w-6 mr-2 sm:mr-3" />
            </td>
            <td class="flex flex-row flex-wrap items-center justify-between px-0 py-0">
              <div class="flex min-w-0 flex-col justify-center">
                <span class="hidden sm:inline">
                  {{ wt('phases', 'label') }} {{ index + 1 }}
                  {{ wt('phases', country, 'phase' + (index + 1), 'name') }}
                </span>
                <span class="sm:hidden"> {{ wt('phases', 'label') }} {{ index + 1 }} </span>
                <span
                  v-if="isMainPhase(index, country)"
                  class="mt-1 whitespace-nowrap text-neutral-450"
                  :class="{ 'text-error-default': !isInRange(index, modelValue, props.serviceCalculator) }">
                  {{ getPhasePercentageRange(index, props.serviceCalculator)?.min }}-{{
                    getPhasePercentageRange(index, props.serviceCalculator)?.max
                  }}%
                </span>
              </div>
              <div v-if="modelValue?.phases[index] && isMainPhase(index, country)" class="">
                <input
                  :value="getRoundedPercentageDisplay(modelValue.phases[index].percentage)"
                  id="percentage"
                  type="number"
                  min="1"
                  max="100"
                  @input="onPhasePercentageInput(index, $event)"
                  @keyup="imposeMinMax(index, modelValue)"
                  @change="imposeMinMax(index, modelValue)"
                  @blur="imposeMinMax(index, modelValue)"
                  class="core-form-input my-2.5 h-10 w-14"
                  :class="{
                    'core-form-error border-error-default text-error-default': !isInRange(
                      index,
                      modelValue,
                      props.serviceCalculator
                    ),
                  }"
                  :disabled="!modelValue.phases[index].active" />
                %
              </div>
            </td>
            <td class="pl-1 text-lg text-right sm:pl-2" :class="{ 'text-neutral-450': !isMainPhase(index, country) }">
              {{ formatHoursPerPhase(index, props.serviceCalculator) }} h
            </td>
          </tr>
          <tr
            class="w-full py-5 mt-10 border border-neutral-300 estimate-total bg-neutral-35 sm:px-5"
            :id="`${props.role.toLowerCase()}_service-estimation-total`">
            <td class="col-span-2 flex items-center justify-between text-black">
              <span>{{ wt('estimation', 'total') }}</span>
              <span class="text-lg text-right text-black"
                >{{ Math.round(props.serviceCalculator.getTotalPercent) }} %</span
              >
            </td>
            <td class="pl-1 text-lg text-right text-black sm:pl-2">
              {{ formatTotalHours(props.serviceCalculator) }} h
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup lang="ts" generic="T extends { phases: ProjectPhase[] }">
import { toRef, type PropType } from 'vue'
import { useProjectGeneralCalculator } from '@/stores/projectCenter/projectGeneralCalculator'
import {
  formatHoursPerPhase,
  formatInitialHours,
  formatTotalHours,
  getPhasePercentageRange,
  imposeMinMax,
  isInRange,
  isMainPhase,
  type ServiceCalculator,
} from '../util'
import type { ProjectPhase } from '@/types'
import { useWidgetText } from '@/helpers/useWidgetText'

export interface Props {
  role: string
  serviceCalculator: ServiceCalculator
}

const props = defineProps<Props>()
const modelValue = defineModel({ type: Object as PropType<T> })
const { wt } = useWidgetText('widget-texts-project-service', toRef(props, 'role'))

const projectGeneralCalculator = useProjectGeneralCalculator()

const country: string = (projectGeneralCalculator.projectGeneral?.country as string) || 'CH'

function getRoundedPercentageDisplay(value?: number): string {
  return value == null ? '' : String(Math.round(value))
}

function onPhasePercentageInput(index: number, event: Event): void {
  const target = event.target as HTMLInputElement
  if (!modelValue.value?.phases?.[index]) return

  if (target.value === '') {
    modelValue.value.phases[index].percentage = undefined
    return
  }

  const parsed = Number(target.value)
  if (!Number.isFinite(parsed)) return

  modelValue.value.phases[index].percentage = parsed
}
</script>

<style lang="postcss" scoped>
.core-service-estimation-table {
  @apply w-full;
  tbody {
    @apply block w-full;
  }
  & tr {
    @apply grid grid-cols-[auto,minmax(0,1fr),5rem] items-center gap-x-3.5 sm:gap-x-6;
    grid-template-rows: auto;
    @apply text-neutral-450;
    &.active {
      @apply text-black;
    }
  }

  & tr.estimate-total {
    @apply text-black;
  }

  & input:disabled {
    @apply text-neutral-450;
  }

  .core-form-checkbox {
    @apply justify-start;
  }

  .core-form-checkbox input::after {
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) rotate(45deg);
  }

  .core-form-error {
    @apply mb-2 w-14;
  }
}
</style>
